import { Document } from '@extrahorizon/javascript-sdk';
import { FdaAllowedDeviceModel } from './general';
import { EicModel } from './profile';

export type MedicalEventStatus =
  /**
   * The event is logged.
   */
  'logged';
export interface MedicalEventData {
  /**
   * Currently there are two possible types. These can be adjusted later on.
   * The types decides which keys are populated w/ the associated data.
   * For now the 'treatment' and 'diagnosis' key will be populated, depending on selected type
   */
  type: 'treatment' | 'diagnostic-result';
  treatment?: {
    reason: 'arrhythmias';
    method: 'catheter-ablation' | 'cardioversion' | 'medication';
    // This is only used for 'catheter-ablation' and 'cardioversion'
    iteration?: 'first' | 'redo';
    // This is only used for the 'medication' treatment
    medication?: {
      type: 'rate-control' | 'anticoagulation' | 'rhythm-control' | 'other';
      protocol: 'titration' | 'daily-dose ' | 'pill-in-pocket';
    };
    // This is only used for the 'catheter-ablation' treatment
    procedure?: {
      type: 'afib' | 'afl' | 'avnrt' | 'avrt' | 'at' | 'lvvt' | 'pvcs' | 'pmvt' | 'other';
      technology: 'rf-ablation' | 'cryoablation' | 'pfa-ablation' | 'laser-ablation' | 'other';
      vendor: 'biosense-webster' | 'boston-scientific' | 'medtronic' | 'abbott' | 'acutus' | 'other';
      blankingPeriodInWeeks: number;
    };
    success?: boolean;
  };
  diagnosis?: {
    reason: 'arrhythmias';
    method:
      | '12-lead-ecg'
      | 'event-recorder'
      | 'holter-monitor-24h'
      | 'holter-monitor-48h'
      | 'holter-monitor-72h'
      | 'holter-monitor-other-duration'
      | 'patch-monitor-7d'
      | 'patch-monitor-14d'
      | 'patch-monitor-other-duration'
      | 'implantable-loop-recorder'
      | 'intermittent-ecg-recording'
      | 'other';
    result?:
      | 'sr'
      | 'afp'
      | 'afx'
      | 'aflp'
      | 'aflx'
      | 'aflap'
      | 'aflax'
      | 'avnrt'
      | 'avrt'
      | 'at'
      | 'svt'
      | 'pve'
      | 'pae'
      | 'avb1'
      | 'avb2'
      | 'avb3'
      | 'other';
  };
  /**
   * The time the data has been logged
   */
  loggedTimestamp: Date;
  /**
   * The patient to whom this data belongs.
   */
  patientId: string;
  /**
   * The group to whom this data belongs.
   */
  groupId: string;
  /**
   * An additional physician comment.
   */
  comment?: string;
}

export type LogsList = Document<MedicalEventData>;

export enum MedicalEventType {
  Treatment = 'treatment',
  DiagnosticResult = 'diagnostic-result',
}

export enum MedicalEventProcedureCatheterAblationType {
  Afib = 'afib',
  Afl = 'afl',
  Avnrt = 'avnrt',
  Avrt = 'avrt',
  At = 'at',
  Lvvt = 'lvvt',
  Pvcs = 'pvcs',
  Pmvt = 'pmvt',
  Other = 'other',
}

export enum MedicalEventProcedureCardioversionType {
  Electrical = 'elec',
  Pharmacological = 'phar',
  Other = 'other',
}

export enum MedicalEventProcedureTechnology {
  RfAblation = 'rf-ablation',
  Cryoablation = 'cryoablation',
  PfaAblation = 'pfa-ablation',
  LaserAblation = 'laser-ablation',
  Other = 'other',
}

export enum MedicalEventProcedureVendor {
  BiosenseWebster = 'biosense-webster',
  BostonScientific = 'boston-scientific',
  Medtronic = 'medtronic',
  Abbott = 'abbott',
  Acutus = 'acutus',
  Other = 'other',
}

export enum MedicalEventTreatmentMethod {
  CatheterAblation = 'catheter-ablation',
  Cardioversion = 'cardioversion',
  Medication = 'medication',
}

export const MedicalEventBlankingPeriod = {
  FourWeeks: 4,
  EightWeeks: 8,
  TwelveWeeks: 12,
};

export enum MedicalEventMedicationType {
  RateControl = 'rate-control',
  Anticoagulation = 'anticoagulation',
  RhythmControl = 'rhythm-control',
  Other = 'other',
}

export enum MedicalEventMedicationProtocol {
  Titration = 'titration',
  DailyDose = 'daily-dose',
  PillInPocket = 'pill-in-pocket',
}

export enum MedicalEventDiagnosticMethod {
  TwelveLeadEcg = '12-lead-ecg',
  EventRecorder = 'event-recorder',
  HolterMonitor24h = 'holter-monitor-24h',
  HolterMonitor48h = 'holter-monitor-48h',
  HolterMonitor72h = 'holter-monitor-72h',
  HolterMonitorOtherDuration = 'holter-monitor-other-duration',
  PatchMonitor7d = 'patch-monitor-7d',
  PatchMonitor12d = 'patch-monitor-14d',
  PatchMonitorOtherDuration = 'patch-monitor-other-duration',
  ImplantableLoopRecorder = 'implantable-loop-recorder',
  IntermittentEcgRecording = 'intermittent-ecg-recording',
  Other = 'other',
}

export enum MedicalEventDiagnosticResult {
  SR = 'sr',
  AFP = 'afp',
  AFX = 'afx',
  AFLP = 'aflp',
  AFLX = 'aflx',
  AFLAP = 'aflap',
  AFLAX = 'aflax',
  AVNRT = 'avnrt',
  AVRT = 'avrt',
  AT = 'at',
  SVT = 'svt',
  PVE = 'pve',
  PAE = 'pae',
  AVB1 = 'avb1',
  AVB2 = 'avb2',
  AVB3 = 'avb3',
  Other = 'other',
}

export enum MedicalEventIteration {
  First = 'first',
  Redo = 'redo',
}

export interface MedicalEventDataForm {
  patientId?: string;
  groupId?: string;
  type?: MedicalEventType;
  loggedTimestamp?: Date;
  treatment?: MedicalEventTreatmentMethod;
  procedure?: MedicalEventProcedureCatheterAblationType;
  iteration?: MedicalEventIteration;
  technology?: MedicalEventProcedureTechnology;
  vendor?: MedicalEventProcedureVendor;
  blankingPeriodInWeeks?: string;
  diagnosticReason?: string;
  diagnosticMethod?: MedicalEventDiagnosticMethod;
  diagnosticResult?: MedicalEventDiagnosticResult;
  medicationProtocol?: MedicalEventMedicationProtocol;
  medicationType?: MedicalEventMedicationType;
  comment?: string;
}

export interface ShouldDisplayPatientFusdrBadgeProps {
  eicInfo?: EicModel;
  fdaAllowedDeviceModels?: FdaAllowedDeviceModel[];
  phoneModel?: string;
}
